import { observable, syncState } from '@legendapp/state';
import { syncObservable } from '@legendapp/state/sync';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { supabase } from '@/lib/supabaseClient';
import { auth$ } from './auth$';
import {
  generateSalt,
  hashPin,
  verifyPinHash,
  encryptSnippets,
  decryptSnippets,
} from '@/lib/encryption';

// PIN state
export const pin$ = observable({
  // Whether user has set up a PIN (loaded from Supabase)
  hasPinSetup: false,
  // Salt for key derivation (loaded from Supabase)
  pinSalt: null,
  // PIN hint (optional)
  pinHint: null,
  // PIN hash for verification (cached locally for offline use)
  pinHash: null,
  // Set of note IDs that are currently unlocked in this session
  // Per-view unlock means this gets cleared when switching notes
  unlockedNoteIds: [],
  // Loading state
  isLoading: true,
  // Error state
  error: null,
  // Failed attempt tracking
  failedAttempts: 0,
  lockedUntil: null, // Timestamp when lockout expires
});

// Persist some PIN state to localStorage (for offline verification)
// NOTE: unlockedNoteIds is intentionally NOT persisted - unlock expires on page refresh
syncObservable(pin$, {
  persist: {
    name: 'promptnote-pin',
    plugin: ObservablePersistLocalStorage,
    transform: {
      save: (value) => {
        // Only persist fields needed for offline verification and anti-bruteforce
        // unlockedNoteIds is excluded so unlock state expires on refresh
        const { pinHash, pinSalt, pinHint, failedAttempts, lockedUntil } = value;
        return { pinHash, pinSalt, pinHint, failedAttempts, lockedUntil };
      },
      load: (value) => ({
        ...value,
        // Force unlockedNoteIds to empty on load - unlock state should not persist
        unlockedNoteIds: [],
      }),
    },
  },
});

// Force clear unlock state after persistence loads
// This ensures unlock expires on page refresh regardless of any stale localStorage data
const pinSyncState$ = syncState(pin$);
pinSyncState$.isPersistLoaded.onChange(({ value: loaded }) => {
  if (loaded) {
    pin$.unlockedNoteIds.set([]);
    console.log('[PIN] Page refresh detected - all notes locked');
  }
});

// Constants
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 30000; // 30 seconds

/**
 * Load PIN settings from Supabase
 */
export const loadPinSettings = async () => {
  const userId = auth$.user.get()?.id;
  if (!userId) {
    pin$.isLoading.set(false);
    return;
  }

  try {
    pin$.isLoading.set(true);
    pin$.error.set(null);

    const { data, error } = await supabase
      .from('user_pin_settings')
      .select('pin_hash, pin_salt, pin_hint')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') {
      // PGRST116 = no rows returned (user has no PIN set up)
      throw error;
    }

    if (data) {
      pin$.hasPinSetup.set(true);
      pin$.pinSalt.set(data.pin_salt);
      pin$.pinHint.set(data.pin_hint);
      pin$.pinHash.set(data.pin_hash);
    } else {
      pin$.hasPinSetup.set(false);
      pin$.pinSalt.set(null);
      pin$.pinHint.set(null);
      pin$.pinHash.set(null);
    }
  } catch (err) {
    console.error('[PIN] Failed to load PIN settings:', err);
    pin$.error.set(err.message);
  } finally {
    pin$.isLoading.set(false);
  }
};

/**
 * Set up a new PIN
 * @param {string} newPin - The PIN to set (min 4 chars)
 * @param {string} hint - Optional hint
 * @returns {Promise<boolean>} Success status
 */
export const setupPin = async (newPin, hint = null) => {
  const userId = auth$.user.get()?.id;
  if (!userId) {
    throw new Error('Must be logged in to set up PIN');
  }

  if (!newPin || newPin.length < 4) {
    throw new Error('PIN must be at least 4 characters');
  }

  try {
    pin$.error.set(null);

    // Generate salt and hash
    const salt = generateSalt();
    const hash = await hashPin(newPin, salt);

    // Upsert to Supabase
    const { error } = await supabase
      .from('user_pin_settings')
      .upsert({
        user_id: userId,
        pin_hash: hash,
        pin_salt: salt,
        pin_hint: hint?.slice(0, 50) || null, // Max 50 chars
        updated_at: new Date().toISOString(),
      });

    if (error) throw error;

    // Update local state
    pin$.hasPinSetup.set(true);
    pin$.pinSalt.set(salt);
    pin$.pinHash.set(hash);
    pin$.pinHint.set(hint?.slice(0, 50) || null);
    pin$.failedAttempts.set(0);
    pin$.lockedUntil.set(null);

    console.log('[PIN] PIN setup successful');
    return true;
  } catch (err) {
    console.error('[PIN] Failed to set up PIN:', err);
    pin$.error.set(err.message);
    throw err;
  }
};

/**
 * Verify a PIN against the stored hash
 * @param {string} inputPin - PIN to verify
 * @returns {Promise<boolean>} True if PIN is correct
 */
export const verifyPin = async (inputPin) => {
  // Check lockout
  const lockedUntil = pin$.lockedUntil.get();
  if (lockedUntil && Date.now() < lockedUntil) {
    const remainingSeconds = Math.ceil((lockedUntil - Date.now()) / 1000);
    throw new Error(`Too many attempts. Try again in ${remainingSeconds} seconds.`);
  }

  const storedHash = pin$.pinHash.get();
  const salt = pin$.pinSalt.get();

  if (!storedHash || !salt) {
    throw new Error('No PIN has been set up');
  }

  try {
    const isValid = await verifyPinHash(inputPin, storedHash, salt);

    if (isValid) {
      // Reset failed attempts on success
      pin$.failedAttempts.set(0);
      pin$.lockedUntil.set(null);
      return true;
    } else {
      // Track failed attempt
      const attempts = pin$.failedAttempts.get() + 1;
      pin$.failedAttempts.set(attempts);

      if (attempts >= MAX_ATTEMPTS) {
        pin$.lockedUntil.set(Date.now() + LOCKOUT_DURATION_MS);
        throw new Error('Too many failed attempts. Please wait 30 seconds.');
      }

      throw new Error(`Incorrect PIN. ${MAX_ATTEMPTS - attempts} attempts remaining.`);
    }
  } catch (err) {
    if (err.message.includes('attempts') || err.message.includes('Try again')) {
      throw err; // Re-throw our custom errors
    }
    console.error('[PIN] Verification error:', err);
    throw new Error('Failed to verify PIN');
  }
};

/**
 * Unlock a note (add to unlocked set for this session)
 * @param {string} noteId - Note ID to unlock
 * @param {string} pin - PIN to verify
 * @returns {Promise<boolean>} Success status
 */
export const unlockNote = async (noteId, pin) => {
  // Verify PIN first
  await verifyPin(pin);

  // Add to unlocked set
  const unlocked = pin$.unlockedNoteIds.get();
  if (!unlocked.includes(noteId)) {
    pin$.unlockedNoteIds.set([...unlocked, noteId]);
  }

  console.log('[PIN] Note unlocked:', noteId);
  return true;
};

/**
 * Lock a note (remove from unlocked set)
 * @param {string} noteId - Note ID to lock
 */
export const lockNote = (noteId) => {
  const unlocked = pin$.unlockedNoteIds.get();
  pin$.unlockedNoteIds.set(unlocked.filter(id => id !== noteId));
  console.log('[PIN] Note locked:', noteId);
};

/**
 * Lock all notes
 */
export const lockAllNotes = () => {
  pin$.unlockedNoteIds.set([]);
  console.log('[PIN] All notes locked');
};

/**
 * Check if a note is currently unlocked
 * @param {string} noteId - Note ID to check
 * @returns {boolean}
 */
export const isNoteUnlocked = (noteId) => {
  return pin$.unlockedNoteIds.get().includes(noteId);
};

/**
 * Protect a note with encryption
 * @param {string} noteId - Note ID to protect
 * @param {Array} snippets - Snippets to encrypt
 * @param {string} pin - PIN for encryption
 * @returns {Promise<Object>} Encrypted content object
 */
export const protectNoteContent = async (noteId, snippets, pin) => {
  const salt = pin$.pinSalt.get();
  if (!salt) {
    throw new Error('No PIN has been set up');
  }

  // Verify PIN first
  await verifyPin(pin);

  // Encrypt snippets
  const encryptedContent = await encryptSnippets(snippets, pin, salt);

  console.log('[PIN] Note content encrypted:', noteId);
  return encryptedContent;
};

/**
 * Decrypt note content
 * @param {Object} encryptedContent - {iv, ciphertext, snippetIds}
 * @param {string} pin - PIN for decryption
 * @returns {Promise<Array>} Decrypted snippet data
 */
export const decryptNoteContent = async (encryptedContent, pin) => {
  const salt = pin$.pinSalt.get();
  if (!salt) {
    throw new Error('No PIN has been set up');
  }

  // Verify PIN
  await verifyPin(pin);

  // Decrypt
  const decryptedData = await decryptSnippets(encryptedContent, pin, salt);

  console.log('[PIN] Content decrypted, snippet count:', decryptedData.length);
  return decryptedData;
};

/**
 * Reset PIN - deletes all protected notes
 * @param {string} confirmText - Must be "DELETE" to confirm
 * @returns {Promise<boolean>} Success status
 */
export const resetPin = async (confirmText) => {
  if (confirmText !== 'DELETE') {
    throw new Error('Please type DELETE to confirm');
  }

  const userId = auth$.user.get()?.id;
  if (!userId) {
    throw new Error('Must be logged in');
  }

  try {
    // First, permanently delete all protected notes
    const { error: deleteNotesError } = await supabase
      .from('notes')
      .delete()
      .eq('user_id', userId)
      .eq('is_protected', true);

    if (deleteNotesError) throw deleteNotesError;

    // Then delete PIN settings
    const { error: deletePinError } = await supabase
      .from('user_pin_settings')
      .delete()
      .eq('user_id', userId);

    if (deletePinError) throw deletePinError;

    // Clear local state
    pin$.hasPinSetup.set(false);
    pin$.pinSalt.set(null);
    pin$.pinHash.set(null);
    pin$.pinHint.set(null);
    pin$.unlockedNoteIds.set([]);
    pin$.failedAttempts.set(0);
    pin$.lockedUntil.set(null);

    console.log('[PIN] PIN reset complete, protected notes deleted');
    return true;
  } catch (err) {
    console.error('[PIN] Reset failed:', err);
    throw err;
  }
};

/**
 * Change PIN (requires current PIN)
 * @param {string} currentPin - Current PIN for verification
 * @param {string} newPin - New PIN to set
 * @param {string} newHint - Optional new hint
 * @returns {Promise<boolean>} Success status
 */
export const changePin = async (currentPin, newPin, newHint = null) => {
  // Verify current PIN
  await verifyPin(currentPin);

  // Set up new PIN (this will generate new salt and re-encrypt)
  await setupPin(newPin, newHint);

  // Note: In a full implementation, we would need to re-encrypt all protected notes
  // with the new key. For now, changing PIN will require unlocking all protected
  // notes to re-encrypt them. This is a UX tradeoff for simplicity.

  console.log('[PIN] PIN changed successfully');
  return true;
};

/**
 * Get list of protected notes (for reset confirmation)
 * @returns {Promise<Array>} Array of {id, title} for protected notes
 */
export const getProtectedNotesList = async () => {
  const userId = auth$.user.get()?.id;
  if (!userId) return [];

  try {
    const { data, error } = await supabase
      .from('notes')
      .select('id, title')
      .eq('user_id', userId)
      .eq('is_protected', true);

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('[PIN] Failed to get protected notes list:', err);
    return [];
  }
};

// Initialize: Load PIN settings when auth changes
auth$.isAuthenticated.onChange(({ value: isAuth }) => {
  if (isAuth) {
    loadPinSettings();
  } else {
    // Clear PIN state on logout
    pin$.hasPinSetup.set(false);
    pin$.pinSalt.set(null);
    pin$.pinHash.set(null);
    pin$.pinHint.set(null);
    pin$.unlockedNoteIds.set([]);
    pin$.isLoading.set(false);
  }
});
